import { PageShell } from '@/components/layout/page-shell';
import { KpiTile } from '@/components/feature/kpi-tile';
import { StreakRing } from '@/components/feature/streak-ring';
import { PracticeCard } from '@/components/feature/practice-card';
import { AssignmentRow } from '@/components/feature/assignment-row';
import { useHome } from '@/hooks/use-home';
import { formatPercent } from '@/lib/format';

export default function HomePage() {
  const { data, isLoading, error } = useHome();

  return (
    <PageShell title="Home">
      {isLoading ? (
        <div className="flex h-48 items-center justify-center text-sm text-[var(--color-muted)]">
          Loading…
        </div>
      ) : error ? (
        <div className="px-4 py-6 text-sm text-[var(--color-destructive)]">
          Couldn't load your home — please try again.
        </div>
      ) : data ? (
        <div className="flex flex-col gap-5 px-4 py-4">
          <section className="flex items-center justify-between rounded-xl bg-[var(--color-secondary-bg)] px-4 py-4">
            <StreakRing current={data.streak.current} longest={data.streak.longest} />
            <div className="ml-4 flex flex-col text-sm">
              <span className="text-[var(--color-muted)]">Current streak</span>
              <span className="mt-0.5 text-lg font-semibold">
                {data.streak.current} {data.streak.current === 1 ? 'day' : 'days'}
              </span>
              {data.streak.days_inactive > 0 ? (
                <span className="mt-1 text-xs text-[var(--color-muted)]">
                  Last activity: {data.streak.days_inactive}d ago
                </span>
              ) : null}
            </div>
          </section>

          <section className="grid grid-cols-2 gap-3">
            <KpiTile label="Week" value={String(data.kpis.sessions_this_week)} hint="sessions" />
            <KpiTile
              label="Done"
              value={formatPercent(data.kpis.completion_rate)}
              hint="30-day rate"
            />
            <KpiTile
              label="Problems"
              value={String(data.kpis.active_problems)}
              hint="active"
            />
            <KpiTile label="Pending" value={String(data.kpis.pending_count)} hint="assignments" />
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-[var(--color-muted)]">
              Today
            </h2>
            {data.today_practices.length === 0 ? (
              <p className="rounded-xl bg-[var(--color-secondary-bg)] px-4 py-6 text-center text-sm text-[var(--color-muted)]">
                Nothing scheduled for today — open the bot to start a new practice.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {data.today_practices.map((p) => (
                  <PracticeCard key={p.id} card={p} />
                ))}
              </div>
            )}
          </section>

          {data.pending_assignments.length > 0 ? (
            <section>
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-[var(--color-muted)]">
                Pending from teachers
              </h2>
              <div className="flex flex-col gap-2">
                {data.pending_assignments.slice(0, 3).map((a) => (
                  <AssignmentRow key={a.id} assignment={a} />
                ))}
              </div>
            </section>
          ) : null}
        </div>
      ) : null}
    </PageShell>
  );
}
